import { Button, cn, Popover, PopoverContent, PopoverTrigger } from '@heroui/react'
import { validationSchemaCustomDateRange } from '@qse/constants'
import { Form, Formik, FormikHelpers } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import FormikControl from '../formik-input'

interface DateRangeFormValues {
  startDate: string
  endDate: string
}

interface DateRangeOption {
  key: string
  label: string
  value: string
}

type DateRangeKey = DateRangeOption['key'] | 'custom' | ''

interface UseDateRangeReturn {
  isCustomRange: boolean
  isOpen: boolean
  selectedRange: DateRangeKey
  handleSelectOption: (key: string) => void
  handleFormSubmit: (
    values: DateRangeFormValues,
    formikHelpers: FormikHelpers<DateRangeFormValues>,
  ) => void
  handleCancel: () => void
  getButtonText: () => string
  setIsOpen: (isOpen: boolean) => void
  customDates: { startDate: string; endDate: string }
  isSwitchingToCustom: boolean
  setIsSwitchingToCustom: (value: boolean) => void
  handleClearFilter: () => void
  hasActiveFilter: boolean
}

interface CreatedAtProps {
  onDateRangeChange?: (range: { createdFrom?: string; createdTo?: string }) => void
  resetTrigger?: number // Add a reset trigger prop
}

const dateRangeOptions: DateRangeOption[] = [
  { key: 'today', label: 'Today', value: 'today' },
  { key: 'last-7-days', label: 'Last 7 days', value: 'last-7-days' },
  { key: 'last-30-days', label: 'Last 30 days', value: 'last-30-days' },
  { key: 'this-year', label: 'This Year', value: 'this-year' },
  { key: 'last-year', label: 'Last Year', value: 'last-year' },
  { key: 'custom', label: 'Custom Date Range', value: 'custom' },
]

const useDateRange = (
  onDateRangeChange?: (range: { createdFrom?: string; createdTo?: string }) => void,
  resetTrigger?: number,
): UseDateRangeReturn => {
  const location = useLocation()
  const [isCustomRange, setIsCustomRange] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectedRange, setSelectedRange] = useState<DateRangeKey>('')
  const [customDates, setCustomDates] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  })
  const [isSwitchingToCustom, setIsSwitchingToCustom] = useState<boolean>(false)

  // Reset state when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setSelectedRange('')
      setCustomDates({ startDate: '', endDate: '' })
      setIsCustomRange(false)
      setIsOpen(false)
      setIsSwitchingToCustom(false)
    }
  }, [resetTrigger])

  // Initialize from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const createdFrom = searchParams.get('createdFrom')
    const createdTo = searchParams.get('createdTo')

    if (!createdFrom || !createdTo) {
      setSelectedRange('')

      return
    }

    // Helper function to get the start of a date (00:00:00)
    const getStartOfDay = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }

    // Helper function to get the end of a date (23:59:59.999)
    const getEndOfDay = (date: Date) => {
      const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())

      endOfDay.setHours(23, 59, 59, 999)

      return endOfDay
    }

    const now = new Date()
    const today = getStartOfDay(now)

    // Generate the exact same date ranges that getDateRange produces
    const presetRanges = [
      {
        key: 'today',
        start: getStartOfDay(today),
        end: getEndOfDay(today),
      },
      {
        key: 'last-7-days',
        start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: getEndOfDay(today),
      },
      {
        key: 'last-30-days',
        start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: getEndOfDay(today),
      },
      {
        key: 'this-year',
        start: new Date(now.getFullYear(), 0, 1),
        end: getEndOfDay(today),
      },
      {
        key: 'last-year',
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: getEndOfDay(new Date(now.getFullYear() - 1, 11, 31)),
      },
    ]

    // Find matching preset range by comparing the exact ISO strings
    const matchingPreset = presetRanges.find(preset => {
      const presetFrom = preset.start.toISOString()
      const presetTo = preset.end.toISOString()

      return createdFrom === presetFrom && createdTo === presetTo
    })

    if (matchingPreset) {
      setSelectedRange(matchingPreset.key)
    } else {
      // Custom range
      setSelectedRange('custom')
      // Convert ISO strings back to date strings for the form
      setCustomDates({
        startDate: new Date(createdFrom).toISOString().split('T')[0],
        endDate: new Date(createdTo).toISOString().split('T')[0],
      })
    }
  }, [location.search])

  const getDateRange = useCallback((key: string) => {
    const now = new Date()

    const getLocalISODate = (date: Date) => {
      // Return full ISO string instead of just the date part
      return date.toISOString()
    }

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (key) {
      case 'today': {
        // For "Today", we need start of day to end of day
        const startOfToday = new Date(today)
        const endOfToday = new Date(today)

        endOfToday.setHours(23, 59, 59, 999)

        return {
          createdFrom: getLocalISODate(startOfToday),
          createdTo: getLocalISODate(endOfToday),
        }
      }

      case 'last-7-days': {
        const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        const endOfToday = new Date(today)

        endOfToday.setHours(23, 59, 59, 999)

        return {
          createdFrom: getLocalISODate(last7Days),
          createdTo: getLocalISODate(endOfToday),
        }
      }

      case 'last-30-days': {
        const last30Days = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
        const endOfToday = new Date(today)

        endOfToday.setHours(23, 59, 59, 999)

        return {
          createdFrom: getLocalISODate(last30Days),
          createdTo: getLocalISODate(endOfToday),
        }
      }

      case 'this-year': {
        const startOfYear = new Date(now.getFullYear(), 0, 1)
        const endOfToday = new Date(today)

        endOfToday.setHours(23, 59, 59, 999)

        return {
          createdFrom: getLocalISODate(startOfYear),
          createdTo: getLocalISODate(endOfToday),
        }
      }

      case 'last-year': {
        const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1)
        const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31)

        endOfLastYear.setHours(23, 59, 59, 999)

        return {
          createdFrom: getLocalISODate(startOfLastYear),
          createdTo: getLocalISODate(endOfLastYear),
        }
      }

      default:
        return {}
    }
  }, [])

  const handleSelectOption = useCallback(
    (key: string) => {
      if (key === 'custom') {
        setIsSwitchingToCustom(true)
        setIsCustomRange(true)
        // Keep popover open for custom range
        setIsOpen(true)
      } else {
        setIsCustomRange(false)
        setSelectedRange(key)
        setIsOpen(false)

        // Get date range and notify parent
        const range = getDateRange(key)

        onDateRangeChange?.(range)
      }
    },
    [getDateRange, onDateRangeChange],
  )

  const handleFormSubmit = useCallback(
    (values: DateRangeFormValues, { setSubmitting }: FormikHelpers<DateRangeFormValues>) => {
      setSelectedRange('custom')
      setCustomDates(values)
      setIsOpen(false)
      setIsSwitchingToCustom(false)
      setSubmitting(false)

      // Convert date strings to ISO strings with proper start and end times
      let startDateISO: string | undefined
      let endDateISO: string | undefined

      if (values.startDate) {
        const startDate = new Date(values.startDate)

        startDateISO = startDate.toISOString()
      }

      if (values.endDate) {
        const endDate = new Date(values.endDate)

        // Set to end of day (23:59:59.999)
        endDate.setHours(23, 59, 59, 999)
        endDateISO = endDate.toISOString()
      }

      // Notify parent with custom date range
      onDateRangeChange?.({
        createdFrom: startDateISO,
        createdTo: endDateISO,
      })
    },
    [onDateRangeChange],
  )

  const handleCancel = useCallback(() => {
    setIsCustomRange(false)
    setIsSwitchingToCustom(false)
  }, [])

  const handleClearFilter = useCallback(() => {
    setSelectedRange('')
    setCustomDates({ startDate: '', endDate: '' })
    setIsCustomRange(false)
    setIsOpen(false)
    setIsSwitchingToCustom(false)

    // Notify parent that filter is cleared
    onDateRangeChange?.({})
  }, [onDateRangeChange])

  const getButtonText = useCallback(() => {
    if (selectedRange === 'custom') {
      if (customDates.startDate && customDates.endDate) {
        return `${customDates.startDate} - ${customDates.endDate}`
      }

      return 'Custom Range'
    }
    const option = dateRangeOptions.find(opt => opt.key === selectedRange)

    return option ? option.label : 'Created'
  }, [selectedRange, customDates])

  // Check if any date filter is active
  const hasActiveFilter = selectedRange !== ''

  return {
    isCustomRange,
    isOpen,
    selectedRange,
    handleSelectOption,
    handleFormSubmit,
    handleCancel,
    getButtonText,
    setIsOpen,
    customDates,
    isSwitchingToCustom,
    setIsSwitchingToCustom,
    handleClearFilter,
    hasActiveFilter,
  }
}

const CreatedAt: React.FC<CreatedAtProps> = ({ onDateRangeChange, resetTrigger }) => {
  const {
    isCustomRange,
    isOpen,
    selectedRange,
    handleSelectOption,
    handleFormSubmit,
    handleCancel,
    getButtonText,
    setIsOpen,
    customDates,
    isSwitchingToCustom,
    setIsSwitchingToCustom,
    handleClearFilter,
    hasActiveFilter,
  } = useDateRange(onDateRangeChange, resetTrigger)

  return (
    <div className="relative">
      <Popover
        isOpen={isOpen}
        placement="bottom-start"
        classNames={{
          content: `bg-white dark:bg-[#343537] border border-gray-300 dark:border-[#707070] text-gray-900 dark:text-gray-300 transition-all duration-500 ease-in-out ${
            isCustomRange ? 'max-w-[500px]' : '!w-[240px]'
          }`,
        }}
        onOpenChange={value => {
          // Prevent closing when switching to custom range mode
          if (!value && isSwitchingToCustom) {
            setIsSwitchingToCustom(false)

            return
          }
          setIsOpen(value)
        }}
      >
        <PopoverTrigger>
          <Button
            aria-label="Select date range"
            className="bg-white dark:bg-[#343537] text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 min-w-full border border-gray-300 dark:border-0"
            size="sm"
            endContent={
              <img alt="calendar" className="filter dark:invert" src="/assets/svg/down-arrow.svg" />
            }
            startContent={
              <img
                alt="calendar"
                className="w-5 h-5 filter dark:invert"
                src="/assets/svg/writing-fluently.svg"
              />
            }
          >
            {getButtonText()}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className={cn(
            'bg-white dark:bg-[#343537] py-2 text-gray-900 dark:text-gray-300',
            isCustomRange ? 'w-[500px]' : 'w-[240px]',
          )}
        >
          <div className={isCustomRange ? 'grid grid-cols-2 gap-4' : ''}>
            <div>
              {dateRangeOptions.map(option => (
                <Button
                  key={option.key}
                  size="sm"
                  variant="light"
                  className={cn(
                    'w-full h-6 justify-start text-left text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600',
                    // Highlight selected option
                    selectedRange === option.key &&
                      'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-medium',
                  )}
                  onPress={() => handleSelectOption(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>

            {isCustomRange && (
              <div className="border-l transition-all duration-300 ease-in-out border-gray-300 dark:border-white pl-4 flex justify-center flex-col">
                <Formik
                  enableReinitialize
                  validationSchema={validationSchemaCustomDateRange}
                  initialValues={{
                    startDate: customDates.startDate,
                    endDate: customDates.endDate,
                  }}
                  onSubmit={handleFormSubmit}
                >
                  {({ isSubmitting, isValid }) => (
                    <Form>
                      <div className="space-y-3">
                        <FormikControl
                          classNameContainer="w-full"
                          control="date"
                          label="Start Date"
                          name="startDate"
                          placeholder="Select start date"
                        />
                        <FormikControl
                          classNameContainer="w-full"
                          control="date"
                          label="End Date"
                          name="endDate"
                          placeholder="Select end date"
                        />
                        <div className="flex justify-end -mr-3">
                          <Button className="bg-transparent -mr-3" size="sm" onPress={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            className="bg-transparent"
                            isDisabled={isSubmitting || !isValid}
                            size="sm"
                            type="submit"
                          >
                            {isSubmitting ? 'Applying...' : 'Apply'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear button for date filter */}
      {hasActiveFilter && (
        <button
          className="absolute -right-2 -top-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
          title="Clear date filter"
          type="button"
          onClick={handleClearFilter}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M6 18L18 6M6 6l12 12"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </button>
      )}
    </div>
  )
}

export default CreatedAt
